

































































































import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { IOrganizations, IUssdCallback, PayloadState } from '@/types/types';
import { UssdAdminService } from '@/services/ussd-admin.service';
import { debounce } from 'lodash';

// New codes are issued under 889 only; 899 is legacy and only appears when editing.
const SHORT_CODE_PREFIX = '*889*';
// Terminated by a single trailing #, with nothing after it: 55#, 551#, 200*7#.
const EXTENSION_PATTERN = /^[0-9*]+#$/;

@Component({ name: 'UssdCallbackDialog' })
export default class UssdCallbackDialog extends Vue {
  @Prop({ required: true, default: false }) state!: boolean;
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: () => [] }) organizations!: IOrganizations[];
  @Prop({ default: null }) callback!: IUssdCallback | null;

  valid: boolean = false;
  form: any = this.emptyForm();
  shortCodeSuffix: string = '';

  checkingAvailability: boolean = false;
  availability: { available: boolean; conflict: any } | null = null;
  // Only the newest request may write a result; answers can land out of order.
  private availabilityRequestId = 0;

  rules = {
    required: (value: string) => !!value || 'This field is required',
    url: (value: string) => /^https?:\/\/.+/.test(value) || 'Must be a valid http(s) URL',
    extension: (value: string) => EXTENSION_PATTERN.test(value || '') || 'Must end with # — e.g. 55#, 551# or 200*7#',
  };

  get isEditing(): boolean {
    return !!this.callback;
  }

  // Legacy 899 codes carry their own prefix, so show those raw.
  get shortCodePrefix(): string {
    return !this.isEditing || String(this.form.shortCode).startsWith(SHORT_CODE_PREFIX) ? SHORT_CODE_PREFIX : '';
  }

  get extensionTaken(): boolean {
    return this.availability?.available === false;
  }

  get availabilityError(): string[] {
    if (!this.extensionTaken) return [];
    const holder = this.availability?.conflict?.shortCode || this.form.shortCode;
    return [`${holder} is already registered`];
  }

  get availabilityHint(): string[] {
    return this.availability?.available ? [`${this.form.shortCode} is available`] : [];
  }

  emptyForm() {
    return {
      serviceCode: '',
      shortCode: '',
      callbackUrl: '',
      ussdVersion: 'LATEST',
      organizationId: null,
    };
  }

  @Watch('state')
  onStateChange(open: boolean): void {
    if (!open) return;
    this.form = this.callback
      ? {
          serviceCode: this.callback.serviceCode,
          shortCode: this.callback.shortCode,
          callbackUrl: this.callback.callbackUrl,
          ussdVersion: this.callback.ussdVersion,
          organizationId: this.callback.organizationId || null,
        }
      : this.emptyForm();
    this.resetAvailability();
    this.shortCodeSuffix = String(this.form.shortCode).slice(this.shortCodePrefix.length);
    (this.$refs.form as Vue & { resetValidation: () => void })?.resetValidation();
  }

  // The service code is the short code stripped of its USSD punctuation.
  @Watch('shortCodeSuffix')
  onShortCodeSuffixChange(suffix: string): void {
    if (this.isEditing) return;
    if (!suffix) {
      this.form.shortCode = '';
      this.form.serviceCode = '';
      this.resetAvailability();
      return;
    }
    this.form.shortCode = `${SHORT_CODE_PREFIX}${suffix}`;
    this.form.serviceCode = this.form.shortCode.replace(/[*#]/g, '');
    // Drops the previous verdict so a stale one cannot sit under a new value.
    this.resetAvailability();
    // Half-typed extensions would only earn a 400, so wait for the closing #.
    if (!EXTENSION_PATTERN.test(suffix)) return;
    this.checkingAvailability = true;
    this.handleOnExtensionChange(suffix);
  }

  private resetAvailability(): void {
    this.handleOnExtensionChange.cancel();
    this.availabilityRequestId++;
    this.checkingAvailability = false;
    this.availability = null;
  }

  // Advisory only — the backend re-checks on create, so a race still 409s.
  async checkExtension(suffix: string): Promise<void> {
    const requestId = ++this.availabilityRequestId;
    try {
      const { data } = await UssdAdminService.checkExtensionAvailability(suffix);
      if (requestId !== this.availabilityRequestId) return;
      this.availability = data?.data ?? null;
    } catch (e) {
      if (requestId !== this.availabilityRequestId) return;
      // A failed check must not block a registration the backend would accept.
      this.availability = null;
    } finally {
      if (requestId === this.availabilityRequestId) this.checkingAvailability = false;
    }
  }

  handleOnExtensionChange = debounce(this.checkExtension, 400);

  beforeDestroy(): void {
    this.resetAvailability();
  }

  @Emit('actions')
  close(payload: PayloadState) {
    return payload;
  }

  @Emit('save')
  save() {
    // editing only sends mutable fields; service/short code identify the registration
    return this.isEditing
      ? {
          id: this.callback!._id,
          body: {
            callbackUrl: this.form.callbackUrl,
            ussdVersion: this.form.ussdVersion,
            ...(this.form.organizationId && { organizationId: this.form.organizationId }),
          },
        }
      : { body: { ...this.form, organizationId: this.form.organizationId || undefined } };
  }
}
